import React, { useState, useEffect } from 'react';
import { Upload, Music, Disc3, Users, Plus, X, Trash2, Loader2 } from 'lucide-react';
import { authService } from '../services/authService';
import { musicService } from '../services/musicService';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ConfirmDialog from '../components/ConfirmDialog';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('artists');
  const [artists, setArtists] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [songs, setSongs] = useState([]);
  
  // Modals
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [showSongModal, setShowSongModal] = useState(false);
  
  // Confirm dialogs
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: '', name: '' });
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Form states
  const [artistForm, setArtistForm] = useState({ name: '', genre: '', bio: '', image: '' });
  const [albumForm, setAlbumForm] = useState({ title: '', artist_id: '', year: new Date().getFullYear(), cover: '' });
  const [songForm, setSongForm] = useState({ title: '', artist_id: '', album_id: '', duration: '', lyrics: '', cover: '', audio_url: '' });
  
  // File uploads
  const [audioFile, setAudioFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (!authService.isAdmin()) {
      toast.error('Acceso denegado - Solo administradores');
      navigate('/');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [artistsData, albumsData, songsData] = await Promise.all([
        musicService.getArtists(),
        musicService.getAlbums(),
        musicService.getSongs()
      ]);
      setArtists(artistsData);
      setAlbums(albumsData);
      setSongs(songsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    }
  };

  // Create Artist
  const handleCreateArtist = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let imageUrl = artistForm.image;
      if (imageFile) {
        toast.info('Subiendo imagen a Cloudinary...');
        const uploadResult = await musicService.uploadImage(imageFile);
        imageUrl = uploadResult.url;
      }
      
      await musicService.createArtist({ ...artistForm, image: imageUrl });
      toast.success('¡Artista creado exitosamente!');
      setShowArtistModal(false);
      setArtistForm({ name: '', genre: '', bio: '', image: '' });
      setImageFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear artista');
    } finally {
      setLoading(false);
    }
  };

  // Create Album
  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let coverUrl = albumForm.cover;
      if (imageFile) {
        toast.info('Subiendo portada a Cloudinary...');
        const uploadResult = await musicService.uploadImage(imageFile);
        coverUrl = uploadResult.url;
      }
      
      await musicService.createAlbum({ ...albumForm, cover: coverUrl });
      toast.success('¡Álbum creado exitosamente!');
      setShowAlbumModal(false);
      setAlbumForm({ title: '', artist_id: '', year: new Date().getFullYear(), cover: '' });
      setImageFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear álbum');
    } finally {
      setLoading(false);
    }
  };

  // Create Song
  const handleCreateSong = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploading(true);
    
    try {
      if (!audioFile) {
        toast.error('Debes seleccionar un archivo de audio');
        setLoading(false);
        setUploading(false);
        return;
      }
      
      toast.info('Subiendo audio a Cloudinary...');
      const audioUpload = await musicService.uploadAudio(audioFile);
      
      let coverUrl = songForm.cover;
      if (imageFile) {
        toast.info('Subiendo portada a Cloudinary...');
        const imageUpload = await musicService.uploadImage(imageFile);
        coverUrl = imageUpload.url;
      } else {
        // Use album cover if no custom cover provided
        const album = albums.find(a => a.id === songForm.album_id);
        coverUrl = album?.cover || '';
      }
      
      await musicService.createSong({ 
        ...songForm, 
        audio_url: audioUpload.url,
        cover: coverUrl
      });
      
      toast.success('¡Canción creada exitosamente!');
      setShowSongModal(false);
      setSongForm({ title: '', artist_id: '', album_id: '', duration: '', lyrics: '', cover: '', audio_url: '' });
      setAudioFile(null);
      setImageFile(null);
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al crear canción';
      if (errorMsg.includes('too large')) {
        toast.error('Archivo muy grande. Máximo 10MB permitido');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  // Delete handlers - Open confirm dialog
  const confirmDelete = (type, id, name) => {
    setDeleteConfirm({ open: true, type, id, name });
  };

  const handleDelete = async () => {
    const { type, id } = deleteConfirm;
    setDeleteConfirm({ open: false, type: '', id: '', name: '' });
    
    try {
      if (type === 'artist') {
        await musicService.deleteArtist(id);
        toast.success('Artista eliminado correctamente');
      } else if (type === 'album') {
        await musicService.deleteAlbum(id);
        toast.success('Álbum eliminado correctamente');
      } else if (type === 'song') {
        await musicService.deleteSong(id);
        toast.success('Canción eliminada correctamente');
      }
      loadData();
    } catch (error) {
      toast.error(`Error al eliminar ${type}`);
    }
  };

  const getDeleteMessage = () => {
    const { type, name } = deleteConfirm;
    const typeLabel = type === 'artist' ? 'artista' : type === 'album' ? 'álbum' : 'canción';
    return `¿Estás seguro de eliminar ${typeLabel === 'artista' ? 'al' : typeLabel === 'álbum' ? 'el' : 'la'} ${typeLabel} "${name}"? Esta acción no se puede deshacer.`;
  };

  return (
    <div className="flex-1 overflow-y-auto pb-32 md:pb-24 bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl md:text-5xl font-black text-gray-900 mb-6 md:mb-8">Panel de Administrador</h1>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
                  <Users size={32} className="text-white" />
                </div>
                <p className="text-4xl font-black text-gray-900">{artists.length}</p>
              </div>
              <p className="text-gray-600 font-semibold">Artistas</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                  <Disc3 size={32} className="text-white" />
                </div>
                <p className="text-4xl font-black text-gray-900">{albums.length}</p>
              </div>
              <p className="text-gray-600 font-semibold">Álbumes</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#00CCFF] to-[#056A84] flex items-center justify-center shadow-lg">
                  <Music size={32} className="text-white" />
                </div>
                <p className="text-4xl font-black text-gray-900">{songs.length}</p>
              </div>
              <p className="text-gray-600 font-semibold">Canciones</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-8">
            <h2 className="text-xl md:text-3xl font-black text-gray-900 mb-4 md:mb-6">Acciones Rápidas</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              <button
                onClick={() => setShowArtistModal(true)}
                className="flex items-center gap-3 md:gap-4 p-4 md:p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl hover:shadow-lg transition-all duration-300 transform active:scale-95 md:hover:-translate-y-1 touch-manipulation"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center shadow-md">
                  <Plus size={24} className="md:w-7 md:h-7 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-black text-gray-900 text-base md:text-lg">Nuevo Artista</p>
                  <p className="text-xs md:text-sm text-gray-600">Agregar artista cristiano</p>
                </div>
              </button>

              <button
                onClick={() => setShowAlbumModal(true)}
                className="flex items-center gap-3 md:gap-4 p-4 md:p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl hover:shadow-lg transition-all duration-300 transform active:scale-95 md:hover:-translate-y-1 touch-manipulation"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-md">
                  <Plus size={24} className="md:w-7 md:h-7 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-black text-gray-900 text-base md:text-lg">Nuevo Álbum</p>
                  <p className="text-xs md:text-sm text-gray-600">Crear álbum de worship</p>
                </div>
              </button>

              <button
                onClick={() => setShowSongModal(true)}
                className="flex items-center gap-3 md:gap-4 p-4 md:p-6 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl hover:shadow-lg transition-all duration-300 transform active:scale-95 md:hover:-translate-y-1 touch-manipulation"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-r from-[#00CCFF] to-[#056A84] flex items-center justify-center shadow-md">
                  <Plus size={24} className="md:w-7 md:h-7 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-black text-gray-900 text-base md:text-lg">Nueva Canción</p>
                  <p className="text-xs md:text-sm text-gray-600">Subir canción cristiana</p>
                </div>
              </button>
            </div>
          </div>

          {/* Content Management */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
            <div className="flex gap-4 border-b border-gray-200 mb-6 overflow-x-auto">
              <button
                onClick={() => setActiveTab('artists')}
                className={`px-6 py-3 font-bold transition-all duration-300 whitespace-nowrap ${
                  activeTab === 'artists'
                    ? 'text-[#00CCFF] border-b-2 border-[#00CCFF]'
                    : 'text-gray-500 hover:text-[#00CCFF]'
                }`}
              >
                Artistas ({artists.length})
              </button>
              <button
                onClick={() => setActiveTab('albums')}
                className={`px-6 py-3 font-bold transition-all duration-300 whitespace-nowrap ${
                  activeTab === 'albums'
                    ? 'text-[#00CCFF] border-b-2 border-[#00CCFF]'
                    : 'text-gray-500 hover:text-[#00CCFF]'
                }`}
              >
                Álbumes ({albums.length})
              </button>
              <button
                onClick={() => setActiveTab('songs')}
                className={`px-6 py-3 font-bold transition-all duration-300 whitespace-nowrap ${
                  activeTab === 'songs'
                    ? 'text-[#00CCFF] border-b-2 border-[#00CCFF]'
                    : 'text-gray-500 hover:text-[#00CCFF]'
                }`}
              >
                Canciones ({songs.length})
              </button>
            </div>

            {/* Artists Tab */}
            {activeTab === 'artists' && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {artists.map((artist) => (
                  <div key={artist.id} className="bg-gray-50 p-4 rounded-xl relative group">
                    <button
                      onClick={() => confirmDelete('artist', artist.id, artist.name)}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110 shadow-lg z-10"
                    >
                      <Trash2 size={16} className="text-white" />
                    </button>
                    <img
                      src={artist.image}
                      alt={artist.name}
                      className="w-full aspect-square object-cover rounded-full mb-3"
                    />
                    <h3 className="font-bold text-gray-900 text-center truncate">{artist.name}</h3>
                    <p className="text-sm text-gray-500 text-center truncate">{artist.genre}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Albums Tab */}
            {activeTab === 'albums' && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {albums.map((album) => (
                  <div key={album.id} className="bg-gray-50 p-4 rounded-xl relative group">
                    <button
                      onClick={() => confirmDelete('album', album.id, album.title)}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110 shadow-lg z-10"
                    >
                      <Trash2 size={16} className="text-white" />
                    </button>
                    <img
                      src={album.cover}
                      alt={album.title}
                      className="w-full aspect-square object-cover rounded-lg mb-3"
                    />
                    <h3 className="font-bold text-gray-900 truncate">{album.title}</h3>
                    <p className="text-sm text-gray-500 truncate">{album.artist_name}</p>
                    <p className="text-xs text-gray-400">{album.year} • {album.tracks} canciones</p>
                  </div>
                ))}
              </div>
            )}

            {/* Songs Tab */}
            {activeTab === 'songs' && (
              <div className="space-y-2">
                {songs.map((song) => (
                  <div key={song.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-200 group">
                    <img src={song.cover} alt={song.title} className="w-12 h-12 rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 truncate">{song.title}</h3>
                      <p className="text-sm text-gray-500 truncate">{song.artist_name} • {song.album_name}</p>
                    </div>
                    <span className="text-sm text-gray-500">{song.duration}</span>
                    <button
                      onClick={() => confirmDelete('song', song.id, song.title)}
                      className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110 shadow-lg"
                    >
                      <Trash2 size={18} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: '', id: '', name: '' })}
        title="Confirmar Eliminación"
        description={getDeleteMessage()}
        onConfirm={handleDelete}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
      />

      {/* Create Artist Modal */}
      <Dialog open={showArtistModal} onOpenChange={setShowArtistModal}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl md:text-2xl font-black text-gray-900">Nuevo Artista</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateArtist} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Nombre del Artista</label>
              <Input
                type="text"
                placeholder="Ej: Hillsong Worship"
                value={artistForm.name}
                onChange={(e) => setArtistForm({ ...artistForm, name: e.target.value })}
                required
                className="rounded-xl text-base"
              />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Género</label>
              <Input
                type="text"
                placeholder="Ej: Worship Contemporáneo"
                value={artistForm.genre}
                onChange={(e) => setArtistForm({ ...artistForm, genre: e.target.value })}
                required
                className="rounded-xl text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Biografía</label>
              <Textarea
                placeholder="Breve descripción del artista..."
                value={artistForm.bio}
                onChange={(e) => setArtistForm({ ...artistForm, bio: e.target.value })}
                rows={3}
                className="rounded-xl text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Imagen del Artista</label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files[0])}
                className="cursor-pointer rounded-xl text-sm"
              />
              {imageFile && (
                <p className="text-xs text-green-600 mt-1">✓ {imageFile.name}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">O ingresa URL:</p>
              <Input
                type="url"
                placeholder="https://ejemplo.com/imagen.jpg"
                value={artistForm.image}
                onChange={(e) => setArtistForm({ ...artistForm, image: e.target.value })}
                className="mt-2 rounded-xl text-base"
              />
            </div>

            <div className="flex flex-col md:flex-row gap-3 pt-2">
              <Button 
                type="submit" 
                disabled={loading} 
                className="w-full md:flex-1 bg-gradient-to-r from-[#00CCFF] to-[#056A84] hover:scale-105 rounded-xl font-bold py-6 touch-manipulation"
              >
                {loading ? 'Creando...' : 'Crear Artista'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowArtistModal(false)}
                className="w-full md:w-auto rounded-xl font-bold py-6 touch-manipulation"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Album Modal */}
      <Dialog open={showAlbumModal} onOpenChange={setShowAlbumModal}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl md:text-2xl font-black text-gray-900">Nuevo Álbum</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAlbum} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Título del Álbum</label>
              <Input
                type="text"
                placeholder="Ej: Awake"
                value={albumForm.title}
                onChange={(e) => setAlbumForm({ ...albumForm, title: e.target.value })}
                required
                className="rounded-xl text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Artista</label>
              <Select
                value={albumForm.artist_id}
                onValueChange={(value) => setAlbumForm({ ...albumForm, artist_id: value })}
              >
                <SelectTrigger className="rounded-xl text-base">
                  <SelectValue placeholder="Selecciona un artista" />
                </SelectTrigger>
                <SelectContent>
                  {artists.map((artist) => (
                    <SelectItem key={artist.id} value={artist.id}>
                      {artist.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Año</label>
              <Input
                type="number"
                min="1900"
                max={new Date().getFullYear() + 1}
                value={albumForm.year}
                onChange={(e) => setAlbumForm({ ...albumForm, year: parseInt(e.target.value) })}
                required
                className="rounded-xl text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Portada del Álbum</label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files[0])}
                className="cursor-pointer rounded-xl text-sm"
              />
              {imageFile && (
                <p className="text-xs text-green-600 mt-1">✓ {imageFile.name}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">O ingresa URL:</p>
              <Input
                type="url"
                placeholder="https://ejemplo.com/portada.jpg"
                value={albumForm.cover}
                onChange={(e) => setAlbumForm({ ...albumForm, cover: e.target.value })}
                className="mt-2 rounded-xl text-base"
              />
            </div>

            <div className="flex flex-col md:flex-row gap-3 pt-2">
              <Button 
                type="submit" 
                disabled={loading} 
                className="w-full md:flex-1 bg-gradient-to-r from-[#00CCFF] to-[#056A84] hover:scale-105 rounded-xl font-bold py-6 touch-manipulation"
              >
                {loading ? 'Creando...' : 'Crear Álbum'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowAlbumModal(false)}
                className="w-full md:w-auto rounded-xl font-bold py-6 touch-manipulation"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Song Modal */}
      <Dialog open={showSongModal} onOpenChange={setShowSongModal}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl md:text-2xl font-black text-gray-900">Nueva Canción</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSong} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Título de la Canción</label>
              <Input
                type="text"
                placeholder="Ej: Reckless Love"
                value={songForm.title}
                onChange={(e) => setSongForm({ ...songForm, title: e.target.value })}
                required
                className="rounded-xl text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Artista</label>
              <Select
                value={songForm.artist_id}
                onValueChange={(value) => setSongForm({ ...songForm, artist_id: value })}
              >
                <SelectTrigger className="rounded-xl text-base">
                  <SelectValue placeholder="Selecciona un artista" />
                </SelectTrigger>
                <SelectContent>
                  {artists.map((artist) => (
                    <SelectItem key={artist.id} value={artist.id}>
                      {artist.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Álbum</label>
              <Select
                value={songForm.album_id}
                onValueChange={(value) => setSongForm({ ...songForm, album_id: value })}
              >
                <SelectTrigger className="rounded-xl text-base">
                  <SelectValue placeholder="Selecciona un álbum" />
                </SelectTrigger>
                <SelectContent>
                  {albums
                    .filter(album => !songForm.artist_id || album.artist_id === songForm.artist_id)
                    .map((album) => (
                      <SelectItem key={album.id} value={album.id}>
                        {album.title}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Duración (mm:ss)</label>
              <Input
                type="text"
                placeholder="Ej: 4:23"
                value={songForm.duration}
                onChange={(e) => setSongForm({ ...songForm, duration: e.target.value })}
                pattern="[0-9]{1,2}:[0-5][0-9]"
                required
                className="rounded-xl text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Archivo de Audio * <span className="text-xs text-gray-500">(Máx 10MB)</span>
              </label>
              <Input
                type="file"
                accept="audio/*,.mp3,.wav,.m4a"
                onChange={(e) => setAudioFile(e.target.files[0])}
                className="cursor-pointer rounded-xl text-sm"
                required
              />
              {audioFile && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ {audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(2)}MB)
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Portada (Opcional) <span className="text-xs text-gray-500">(Máx 10MB)</span>
              </label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files[0])}
                className="cursor-pointer rounded-xl text-sm"
              />
              {imageFile && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ {imageFile.name} ({(imageFile.size / 1024 / 1024).toFixed(2)}MB)
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Letra (Opcional)</label>
              <Textarea
                placeholder="Letra de la canción..."
                value={songForm.lyrics}
                onChange={(e) => setSongForm({ ...songForm, lyrics: e.target.value })}
                rows={4}
                className="rounded-xl text-base"
              />
            </div>

            <div className="flex flex-col md:flex-row gap-3 pt-2">
              <Button 
                type="submit" 
                disabled={loading || uploading} 
                className="w-full md:flex-1 bg-gradient-to-r from-[#00CCFF] to-[#056A84] hover:scale-105 rounded-xl font-bold py-6 touch-manipulation"
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  'Crear Canción'
                )}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowSongModal(false)} 
                disabled={uploading}
                className="w-full md:w-auto rounded-xl font-bold py-6 touch-manipulation"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
